import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { allProducts, productCategories } from '@/mocks/products';
import ProductCard from '@/components/base/ProductCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ProductsPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    document.title = t('products_title') || 'CSSBuy Spreadsheet Products | cssbuybr.my';
  }, [t]);

  const filteredProducts = allProducts.filter(p => {
    const matchCategory = activeCategory === 'All' || p.category === activeCategory;
    const matchSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  const categories = ['All', ...productCategories];

  const productCountText = (count: number) => {
    if (count === 1) return t('one_product') || '1 product';
    return `${count} ${t('many_products') || 'products'}`;
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="pt-24 md:pt-32 pb-4 md:pb-8 px-4 md:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              {t('products_title') || 'CSSBuy Spreadsheet Products'}
            </h1>
            <p className="text-lg text-foreground-600 max-w-2xl mx-auto">
              {t('products_subtitle') || 'Explore all products from the CSSBuy Spreadsheet. Filter by category and click any item to view details and purchase.'}
            </p>
          </div>
        </section>

        <section className="px-4 md:px-8 lg:px-12 pb-4">
          <div className="max-w-7xl mx-auto">
            <div className="relative max-w-md mx-auto">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('search_placeholder') || 'Search product, category or keyword...'}
                className="w-full pl-10 pr-4 py-3 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                >
                  <i className="ri-close-circle-line" />
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-8 lg:px-12 sticky top-16 md:top-20 bg-background-50/95 backdrop-blur-sm z-30 py-4 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat === 'All' ? (t('filter_all') || 'All') : cat}
                </button>
              ))}
            </div>
            <p className="text-sm text-foreground-500 mt-2">
              {productCountText(filteredProducts.length)}
              {activeCategory !== 'All' && ` in ${activeCategory}`}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>
        </section>

        <section className="px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {filteredProducts.length > 0 ? (
              filteredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))
            ) : (
              <div className="col-span-full text-center py-16">
                <i className="ri-search-line text-4xl text-foreground-300 mb-3" />
                <p className="text-foreground-500 text-lg">{t('no_results') || 'No results found'}</p>
              </div>
            )}
          </div>
        </section>

        <section className="px-4 md:px-8 lg:px-12 pb-12">
          <div className="max-w-7xl mx-auto text-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 text-white font-medium rounded-lg hover:bg-secondary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-up-line" />
              {t('back_to_top') || 'Back to Top'}
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}